/* eslint-disable camelcase */
import { PersonalizedDdp_DegreeApplicationStatus } from '__generated__/graphql-types';

import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import type { UniversityProgramDeadlines } from 'bundles/degree-description/__generated__/baseContentfulTypes';
import type {
  PersonalizedButtonClicks,
  PersonalizedData,
  PersonalizedDdpViews,
  PersonalizedDegreeApplications,
  PersonalizedEoiResponses,
  PersonalizedStackableContent,
} from 'bundles/degree-description/types';
import {
  getPersonalizedDdpApplyVariant,
  previewPersonalizedDdpApplyVariant,
} from 'bundles/degree-description/utils/experiments';
import type {
  PersonalizedMessage,
  PersonalizedMessageActionType,
  PersonalizedMessageName,
} from 'bundles/degree-description/utils/personalizedMessageContent';
import { personalizedMessages } from 'bundles/degree-description/utils/personalizedMessageContent';
import {
  getClosedPersonalizedMessageCount,
  getCurrentPersonalizedMessages,
  getMessageViewCount,
  incrementMessageViewCount,
  setCurrentPersonalizedMessages,
} from 'bundles/degree-description/utils/personalizedMessageViews';
import type { ActionConfigProps, EoiButtonType, EoiButtonsType } from 'bundles/expression-of-interest/types';

export type MessagesToDisplay = {
  modalMessageToDisplay: PersonalizedMessage | null;
  embeddedMessageToDisplay: PersonalizedMessage | null;
  tooltipMessageToDisplay: PersonalizedMessage | null;
};

export const hasEnrolledStackableRelatedTo = (enrolledStackableContent?: PersonalizedStackableContent | null) => {
  const enrolledStackableCourses = enrolledStackableContent?.relatedToContents?.courses;
  const enrolledStackableMts = enrolledStackableContent?.relatedToContents?.masterTracks;
  const enrolledStackableS12ns = enrolledStackableContent?.relatedToContents?.s12ns;
  if (enrolledStackableS12ns?.length && enrolledStackableS12ns?.length > 0) {
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return personalizedMessages.enrolledStackableRelatedTo(enrolledStackableS12ns[0].name);
  }
  if (enrolledStackableMts?.length && enrolledStackableMts?.length > 0) {
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return personalizedMessages.enrolledStackableRelatedTo(enrolledStackableMts[0].name);
  }
  if (enrolledStackableCourses?.length && enrolledStackableCourses?.length > 0) {
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return personalizedMessages.enrolledStackableRelatedTo(enrolledStackableCourses[0].name);
  }
  return null;
};

export const hasEnrolledStackableOffersCredit = (
  enrolledStackableContent?: PersonalizedStackableContent | null,
  programName?: string | null,
  partnerName?: string | null
) => {
  const enrolledStackableCourses = enrolledStackableContent?.offersCreditContents?.courses;
  const enrolledStackableMts = enrolledStackableContent?.offersCreditContents?.masterTracks;
  const enrolledStackableS12ns = enrolledStackableContent?.offersCreditContents?.s12ns;
  if (enrolledStackableS12ns?.length && enrolledStackableS12ns?.length > 0 && programName && partnerName) {
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return personalizedMessages.enrolledStackableOffersCredit(enrolledStackableS12ns[0].name, programName, partnerName);
  }
  if (enrolledStackableMts?.length && enrolledStackableMts?.length > 0 && programName && partnerName) {
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return personalizedMessages.enrolledStackableOffersCredit(enrolledStackableMts[0].name, programName, partnerName);
  }
  if (enrolledStackableCourses?.length && enrolledStackableCourses?.length > 0 && programName && partnerName) {
    return personalizedMessages.enrolledStackableOffersCredit(
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      enrolledStackableCourses[0].name,
      programName,
      partnerName
    );
  }
  return null;
};

export const hasEnrolledStackablePartOf = (enrolledStackableContent?: PersonalizedStackableContent | null) => {
  const enrolledStackableCourses = enrolledStackableContent?.partOfContents?.courses;
  const enrolledStackableMts = enrolledStackableContent?.partOfContents?.masterTracks;
  const enrolledStackableS12ns = enrolledStackableContent?.partOfContents?.s12ns;
  if (enrolledStackableS12ns?.length && enrolledStackableS12ns?.length > 0) {
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return personalizedMessages.enrolledStackablePartOf(enrolledStackableS12ns[0].name);
  }
  if (enrolledStackableMts?.length && enrolledStackableMts?.length > 0) {
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return personalizedMessages.enrolledStackablePartOf(enrolledStackableMts[0].name);
  }
  if (enrolledStackableCourses?.length && enrolledStackableCourses?.length > 0) {
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return personalizedMessages.enrolledStackablePartOf(enrolledStackableCourses[0].name);
  }
  return null;
};

export const hasViewedDdpTwiceMax = (ddpViews?: PersonalizedDdpViews | null) => {
  if (ddpViews && (ddpViews.pageViews === 1 || ddpViews.pageViews === 2)) {
    return personalizedMessages.viewedDdpTwiceMax();
  }
  return null;
};

export const hasViewedDdpAboveTwice = (ddpViews?: PersonalizedDdpViews | null) => {
  if (ddpViews?.pageViews && ddpViews.pageViews > 2) {
    return personalizedMessages.viewedDdpAboveTwice();
  }
  return null;
};

export const hasViewedDdpSubPages = (ddpViews?: PersonalizedDdpViews | null) => {
  const mostViews = Math.max(
    ddpViews?.admissionTabViews || 0,
    ddpViews?.academicsTabViews || 0,
    ddpViews?.studentExperienceTabViews || 0,
    ddpViews?.careerTabViews || 0,
    ddpViews?.tuitionTabViews || 0
  );

  if (mostViews > 0) {
    let mostViewedTab;
    if (mostViews === ddpViews?.admissionTabViews) {
      mostViewedTab = 'admissions';
    } else if (mostViews === ddpViews?.academicsTabViews) {
      mostViewedTab = 'academics';
    } else if (mostViews === ddpViews?.studentExperienceTabViews) {
      mostViewedTab = 'student experience';
    } else if (mostViews === ddpViews?.careerTabViews) {
      mostViewedTab = 'careers';
    } else {
      mostViewedTab = 'tuition & financing';
    }
    return personalizedMessages.viewedDdpSubPages(mostViewedTab);
  }
  return null;
};

// user has clicked on EOI button but not submitted
export const hasClickedEoi = (eoiResponses?: PersonalizedEoiResponses | null) => {
  if (eoiResponses && eoiResponses.length > 0 && eoiResponses?.every((response) => response.completed === false)) {
    return personalizedMessages.clickedEoi();
  }
  return null;
};

export const hasSubmittedEoi = (eoiResponses?: PersonalizedEoiResponses | null) => {
  if (eoiResponses?.some((response) => response.completed === true)) {
    return personalizedMessages.submittedEoi();
  }
  return null;
};

export const hasClickedApply = (buttonClicks?: PersonalizedButtonClicks | null) => {
  if (buttonClicks?.clickedApplyButton === true) {
    return personalizedMessages.clickedApply();
  }
  return null;
};

export const hasStartedButNotSubmittedApplication = (
  degreeApplications?: PersonalizedDegreeApplications | null,
  applicationDeadline?: string | null
) => {
  if (!degreeApplications) {
    return null;
  }

  for (const degreeApplication of degreeApplications) {
    if (
      (degreeApplication.applicationStatus === PersonalizedDdp_DegreeApplicationStatus.AwaitingSubmission ||
        degreeApplication.applicationSubmittedDate === null) &&
      applicationDeadline
    ) {
      return personalizedMessages.startedApply(applicationDeadline);
    }
  }
  return null;
};

export const getEoiButton = (
  ctaActionType?: PersonalizedMessageActionType | null,
  ctaText?: string | null,
  buttonType?: string,
  eoiButtons?: EoiButtonsType,
  onClick?: () => void
) => {
  let eoiButtonConfig: EoiButtonType | undefined;

  const applyButtonConfig = eoiButtons?.filter((eoiButton) => {
    if (eoiButton?.actionType === 'apply') {
      return eoiButton;
    } else {
      return undefined;
    }
  })?.[0];

  const requestInfoButtonConfig = eoiButtons?.filter((eoiButton) => {
    if (eoiButton?.actionType === 'emailMeInfo') {
      return eoiButton;
    } else {
      return undefined;
    }
  })?.[0];

  if (ctaActionType === 'ENROLL_APPLY' && applyButtonConfig) {
    eoiButtonConfig = applyButtonConfig;
  } else if (ctaActionType === 'REQUEST_INFO' && requestInfoButtonConfig) {
    eoiButtonConfig = requestInfoButtonConfig;
  }

  if (!eoiButtonConfig) return undefined;

  const onClickWithExtraAction = (extraFunc: (() => void) | undefined) => {
    if (extraFunc) {
      return () => {
        eoiButtonConfig?.buttonOptions?.onClick?.(eoiButtonConfig?.actionType);
        extraFunc();
      };
    } else {
      return eoiButtonConfig?.buttonOptions?.onClick;
    }
  };

  const eoiButtonConfigCustomized: ActionConfigProps = {
    ...eoiButtonConfig,
    buttonOptions: {
      ...eoiButtonConfig.buttonOptions,
      type: buttonType ?? eoiButtonConfig.buttonOptions?.type,
      label: ctaText ?? eoiButtonConfig.buttonOptions?.label,
      onClick: onClickWithExtraAction(onClick),
      trackingData: {
        ...eoiButtonConfig?.buttonOptions?.trackingData,
        currentPersonalizedMessages: getCurrentPersonalizedMessages(),
        personalizedMessageViewCount: getMessageViewCount(),
      },
    },
  };

  return eoiButtonConfigCustomized;
};

export const getCtaActionXddp = (
  ctaActionType?: PersonalizedMessageActionType | null,
  applyEnrollAction?: string | null,
  requestInfoAction?: (() => void) | string | null
) => {
  if (ctaActionType === 'ENROLL_APPLY') {
    return applyEnrollAction;
  } else if (ctaActionType === 'REQUEST_INFO') {
    return requestInfoAction;
  } else {
    return undefined;
  }
};

// returns a list of all of the messages that exist, sorted by priority
export const getPersonalizedMessages = (
  PersonalizedDdpData: PersonalizedData,
  slug?: string | null,
  programName?: string | null,
  partnerName?: string | null,
  deadlineinfo?: UniversityProgramDeadlines[]
) => {
  const enrolledStackableContent = PersonalizedDdpData?.enrolledStackableContent;
  const ddpViews = PersonalizedDdpData?.ddpViews;
  const eoiResponses = PersonalizedDdpData?.eoiResponses;
  const buttonClicks = PersonalizedDdpData?.buttonClicks;
  const degreeApplications = PersonalizedDdpData?.degreeApplications;

  const applyMessageType = previewPersonalizedDdpApplyVariant(slug);
  let hasApplyMessages = false;

  let applicationDeadline = null;
  if (deadlineinfo && deadlineinfo) {
    const upcomingDeadlines = deadlineinfo.filter(
      (deadline) => deadline?.enrollApplyEnd && new Date(deadline.enrollApplyEnd) > new Date()
    );
    if (upcomingDeadlines.length > 0 && upcomingDeadlines[0]) {
      applicationDeadline = formatDateTimeDisplay(
        upcomingDeadlines[0].enrollApplyEnd,
        MOMENT_FORMATS.LONG_DATE_ONLY_DISPLAY
      );
    }
  }

  let allPossibleMessages: (PersonalizedMessage | null)[] = [
    hasViewedDdpTwiceMax(ddpViews),
    hasViewedDdpAboveTwice(ddpViews),
    hasViewedDdpSubPages(ddpViews),
    hasClickedEoi(eoiResponses),
  ];

  if (applyMessageType !== 'none') {
    const possibleApplyMessages = [
      hasEnrolledStackableRelatedTo(enrolledStackableContent),
      hasEnrolledStackableOffersCredit(enrolledStackableContent, programName, partnerName),
      hasEnrolledStackablePartOf(enrolledStackableContent),
      hasSubmittedEoi(eoiResponses),
      hasClickedApply(buttonClicks),
      hasStartedButNotSubmittedApplication(degreeApplications, applicationDeadline),
    ];
    const validApplyMessages = possibleApplyMessages.filter((message) => message !== null) as PersonalizedMessage[];
    if (validApplyMessages.length > 0) {
      hasApplyMessages = true;
      allPossibleMessages = allPossibleMessages.concat(validApplyMessages);
    }
  }

  if (allPossibleMessages.length > 0) {
    const validMessages = allPossibleMessages.filter((message) => message !== null) as PersonalizedMessage[];
    const sortedMessages = validMessages.sort((a, b) => b.priority - a.priority);
    return { messages: sortedMessages, hasApplyMessages };
  }
  return { messages: null, hasApplyMessages };
};

// Function to determine which message of each type to display
export const getMessagesToDisplay = (
  PersonalizedDdpData: PersonalizedData,
  slug?: string | null,
  programName?: string | null,
  partnerName?: string | null,
  deadlineinfo?: UniversityProgramDeadlines[],
  isMobile?: boolean
) => {
  const { messages, hasApplyMessages } = getPersonalizedMessages(
    PersonalizedDdpData,
    slug,
    programName,
    partnerName,
    deadlineinfo
  );

  const applyMessageType = getPersonalizedDdpApplyVariant(slug);
  const currentCloseCounts = getClosedPersonalizedMessageCount();

  let messageType = 'embedded';
  let currentPersonalizedMessages: PersonalizedMessageName[] = [];

  let messagesToDisplay: MessagesToDisplay = {
    modalMessageToDisplay: null,
    embeddedMessageToDisplay: null,
    tooltipMessageToDisplay: null,
  };

  if (applyMessageType !== 'none' && hasApplyMessages) {
    messageType = applyMessageType;
  }

  if (isMobile) {
    const embeddedMessageToDisplay = messages?.[0] || null;
    messagesToDisplay = {
      modalMessageToDisplay: null,
      embeddedMessageToDisplay,
      tooltipMessageToDisplay: null,
    };
  } else if (messageType === 'embedded') {
    const embeddedMessageToDisplay = messages?.[0] || null;
    messagesToDisplay = {
      modalMessageToDisplay: null,
      embeddedMessageToDisplay,
      tooltipMessageToDisplay: null,
    };
  } else if (messageType === 'tooltip') {
    let tooltipMessageToDisplay = messages?.[0] || null;
    if (tooltipMessageToDisplay && slug) {
      const closeCount = currentCloseCounts?.[slug]?.[tooltipMessageToDisplay?.id] || 0;
      if (closeCount >= 2) {
        tooltipMessageToDisplay = null;
      }
    }
    messagesToDisplay = {
      modalMessageToDisplay: null,
      embeddedMessageToDisplay: null,
      tooltipMessageToDisplay,
    };
  }

  // always display the highest priority embedded message, then display one additional message (tooltip or modal) based on priority
  else if (messageType === 'mixed') {
    const embeddedMessageToDisplay = messages?.find((message) => message.component === 'EMBEDDED') || null;
    let tooltipMessageToDisplay = messages?.find((message) => message.component === 'TOOLTIP') || null;
    let modalMessageToDisplay = messages?.find((message) => message.component === 'MODAL') || null;
    if (tooltipMessageToDisplay && slug) {
      const closeCount = currentCloseCounts?.[slug]?.[tooltipMessageToDisplay?.id] || 0;
      if (closeCount >= 2) {
        tooltipMessageToDisplay = null;
      }
    }
    if (modalMessageToDisplay && slug) {
      const closeCount = currentCloseCounts?.[slug]?.[modalMessageToDisplay?.id] || 0;
      if (closeCount >= 2) {
        modalMessageToDisplay = null;
      }
    }
    if (tooltipMessageToDisplay?.priority && modalMessageToDisplay?.priority) {
      if (tooltipMessageToDisplay.priority >= modalMessageToDisplay.priority) {
        messagesToDisplay = {
          modalMessageToDisplay: null,
          embeddedMessageToDisplay,
          tooltipMessageToDisplay,
        };
      } else {
        messagesToDisplay = {
          modalMessageToDisplay,
          embeddedMessageToDisplay,
          tooltipMessageToDisplay: null,
        };
      }
    } else {
      messagesToDisplay = {
        modalMessageToDisplay,
        embeddedMessageToDisplay,
        tooltipMessageToDisplay,
      };
    }
  } else
    messagesToDisplay = {
      modalMessageToDisplay: null,
      embeddedMessageToDisplay: null,
      tooltipMessageToDisplay: null,
    };

  if (slug) {
    if (messagesToDisplay.embeddedMessageToDisplay !== null) {
      incrementMessageViewCount(slug, messagesToDisplay.embeddedMessageToDisplay.id);
      currentPersonalizedMessages = currentPersonalizedMessages.concat(messagesToDisplay.embeddedMessageToDisplay.id);
    }
    if (messagesToDisplay.tooltipMessageToDisplay !== null) {
      incrementMessageViewCount(slug, messagesToDisplay.tooltipMessageToDisplay.id);
      currentPersonalizedMessages = currentPersonalizedMessages.concat(messagesToDisplay.tooltipMessageToDisplay.id);
    }
    if (messagesToDisplay.modalMessageToDisplay !== null) {
      incrementMessageViewCount(slug, messagesToDisplay.modalMessageToDisplay.id);
      currentPersonalizedMessages = currentPersonalizedMessages.concat(messagesToDisplay.modalMessageToDisplay.id);
    }
    setCurrentPersonalizedMessages(slug, currentPersonalizedMessages);
  }
  return messagesToDisplay;
};
